import { CalendarDays, CheckCheck, Mail, Settings, Users, Workflow, type LucideIcon } from 'lucide-react';
import { MODULE_DEFINITIONS, canView, type ModuleState, type PermissionObject, type SessionPayload } from '@nest-recruit/shared';
import { isRequirementOn, moduleForPath } from '../../lib/module-map';

// The Admin frame's menu, as data (Admin redesign 2026-11-02, FRAME-SPEC §2; DECISIONS-LOG 2026-11-02 Q1).
//
// One list read by the menu, the laptop rail, the phone sheet, "Find a setting" and the landing redirect, so
// what the menu shows, what Find offers and where `/admin` lands can never disagree. Every section is gated
// exactly as the old tabs were: `canView` on its §L23 object, and its module from `lib/module-map.ts`.

export type AdminSectionId =
  | 'overview'
  | 'people'
  | 'roles'
  | 'delegation'
  | 'access-log'
  | 'pipelines'
  | 'reasons'
  | 'sources'
  | 'role-templates'
  | 'pools-and-tags'
  | 'custom-fields'
  | 'career-site'
  | 'tool-names'
  | 'staff'
  | 'interview-plans'
  | 'rubrics'
  | 'interview-kits'
  | 'calendars'
  | 'rooms'
  | 'approval-chains'
  | 'time-limits'
  | 'offer-letters'
  | 'message-templates'
  | 'email'
  | 'inbox'
  | 'master-settings'
  | 'settings'
  | 'fx-rates'
  | 'name-matching'
  | 'retention'
  | 'events'
  | 'background-work';

export interface AdminSection {
  readonly id: AdminSectionId;
  readonly label: string;
  readonly to: string;
  /** §L23: shown only to someone who may open it (S32). */
  readonly object: PermissionObject;
  /** One plain line: the rail's pop-up and "Find a setting" show it under the name. */
  readonly hint: string;
  /** FRAME-SPEC §2: the one-line sublabel drawn under the name in the menu itself. */
  readonly sublabel?: string;
  /** A server page, not a screen of this app, so it opens in a new tab (S8). No section uses it since Background work. */
  readonly isExternal?: boolean;
  /** Words people look for it by that are not in its name (FRAME-SPEC §2's table). */
  readonly keywords?: readonly string[];
  /** Settings inside the section, so Find answers "Reporting currency" with System settings. */
  readonly settings?: readonly string[];
  /** Other screens that belong to this item: People's invite page. */
  readonly alsoActiveOn?: readonly string[];
  /** How a red to-do count on this item reads aloud. */
  readonly countLabel?: (count: number) => string;
}

export interface AdminGroup {
  readonly id: string;
  readonly label: string;
  /** The rail's caption at laptop width (FRAME-SPEC §5). */
  readonly railLabel: string;
  readonly icon: LucideIcon;
  readonly sections: readonly AdminSection[];
}

/** Bull Board — the technical background-job dashboard (S8), mounted on Fastify; Background work's "Technical view ↗". */
export const QUEUE_DASHBOARD_PATH = '/admin/queues';

export const ADMIN_OVERVIEW: AdminSection = {
  id: 'overview',
  label: 'Overview',
  to: '/admin/overview',
  // Brief 2026-11-02 §3: Overview is Admin only; HR keeps landing on their first page.
  object: 'admin_config',
  hint: 'What needs your attention, and where things stand',
  keywords: ['home', 'attention', 'health', 'dashboard', 'summary'],
};

const plural = (count: number, one: string, many: string): string => `${count} ${count === 1 ? one : many}`;

export const ADMIN_GROUPS: readonly AdminGroup[] = [
  {
    id: 'people',
    label: 'People and access',
    railLabel: 'People',
    icon: Users,
    sections: [
      {
        id: 'people',
        label: 'People',
        to: '/admin/users',
        object: 'user_management',
        hint: 'Everyone who can sign in, and invitations',
        keywords: ['users', 'staff', 'accounts', 'invite', 'deactivate', 'login', 'password', 'locked'],
        alsoActiveOn: ['/admin/invite'],
        countLabel: (count) => plural(count, 'invitation not accepted yet', 'invitations not accepted yet'),
      },
      {
        id: 'roles',
        label: 'Roles',
        to: '/admin/roles',
        object: 'user_management',
        hint: 'What each role can see and do',
        keywords: ['permissions', 'access', 'matrix', 'field rules'],
      },
      {
        id: 'delegation',
        label: 'Delegation',
        to: '/admin/delegation',
        object: 'user_management',
        hint: 'Who approves while someone is away',
        keywords: ['approvals', 'cover', 'away', 'leave', 'delegate', 'out of office'],
      },
      {
        id: 'access-log',
        label: 'Access log',
        to: '/admin/audit',
        object: 'audit_log',
        hint: 'Sign-ins, refused actions and viewing as someone',
        keywords: ['audit', 'history', 'who changed', 'who viewed', 'sign-ins', 'refused'],
      },
    ],
  },
  {
    id: 'hiring',
    label: 'Hiring setup',
    railLabel: 'Hiring',
    icon: Workflow,
    sections: [
      {
        id: 'pipelines',
        label: 'Pipelines',
        to: '/admin/pipelines',
        object: 'hiring_setup',
        hint: 'The stages a candidate moves through',
        keywords: ['stages', 'steps'],
      },
      {
        id: 'reasons',
        label: 'Reasons',
        to: '/admin/reasons',
        object: 'hiring_setup',
        hint: 'Rejection and withdrawal reasons',
        keywords: ['rejection', 'withdrawal', 'regret'],
      },
      {
        id: 'sources',
        label: 'Sources',
        to: '/admin/sources',
        object: 'hiring_setup',
        hint: 'Where candidates come from',
        keywords: ['job board', 'bdjobs', 'linkedin', 'channel'],
      },
      {
        id: 'role-templates',
        label: 'Role templates',
        to: '/admin/role-templates',
        object: 'hiring_setup',
        hint: 'Ready-made jobs to start from',
        keywords: ['template', 'job template'],
      },
      {
        id: 'pools-and-tags',
        label: 'Pools and tags',
        to: '/admin/pools-and-tags',
        object: 'hiring_setup',
        hint: 'Talent pools and the tags on candidates',
        keywords: ['talent pool', 'tag', 'label'],
      },
      {
        id: 'custom-fields',
        label: 'Custom fields',
        to: '/admin/custom-fields',
        object: 'admin_config',
        hint: 'Extra fields on records',
        keywords: ['extra field', 'more details', 'form'],
      },
      {
        id: 'career-site',
        label: 'Career site',
        to: '/admin/career-site',
        object: 'hiring_setup',
        hint: 'The public jobs page and its postings',
        keywords: ['careers page', 'postings', 'apply form', 'website'],
      },
      {
        id: 'tool-names',
        label: 'Tools and systems',
        to: '/admin/tool-names',
        object: 'hiring_setup',
        hint: 'The tool names a hire request offers',
        keywords: ['tool', 'software', 'typo', 'merge'],
      },
      {
        id: 'staff',
        label: 'Staff list',
        to: '/admin/staff',
        object: 'hiring_setup',
        hint: 'The staff sheet: who reports to whom',
        keywords: ['employees', 'reports to', 'manager', 'upload'],
      },
    ],
  },
  {
    id: 'interviews',
    label: 'Interviews and calendars',
    railLabel: 'Interviews',
    icon: CalendarDays,
    sections: [
      {
        id: 'interview-plans',
        label: 'Interview plans',
        to: '/admin/interview-plans',
        object: 'hiring_setup',
        hint: 'Ready-made interview rounds for a job',
        keywords: ['template', 'rounds'],
      },
      {
        id: 'rubrics',
        label: 'Rubrics',
        to: '/admin/rubrics',
        object: 'hiring_setup',
        hint: 'The lines kits group their questions under',
        keywords: ['scorecard', 'scoring'],
      },
      {
        id: 'interview-kits',
        label: 'Interview kits',
        to: '/admin/interview-kits',
        object: 'hiring_setup',
        hint: 'Questions and guidance for interviewers',
        keywords: ['questions', 'guide'],
      },
      {
        id: 'calendars',
        label: 'Calendars',
        to: '/admin/calendars',
        object: 'hiring_setup',
        hint: 'Working hours and holidays',
        keywords: ['holiday', 'working hours', 'working days', 'weekend'],
        settings: ['Working hours', 'Holidays', 'Which calendar applies'],
      },
      {
        id: 'rooms',
        label: 'Rooms',
        to: '/admin/rooms',
        object: 'hiring_setup',
        hint: 'Interview rooms',
        keywords: ['meeting room', 'venue'],
      },
    ],
  },
  {
    id: 'approvals',
    label: 'Approvals and offers',
    railLabel: 'Approvals',
    icon: CheckCheck,
    sections: [
      {
        // Admin redesign 2026-11-02: shown while Approval chains (hire-request chains) or Offers (offer chains) is on.
        id: 'approval-chains',
        label: 'Approval chains',
        to: '/admin/approval-chains',
        object: 'admin_config',
        hint: 'Who approves a hire request or an offer',
        keywords: ['approvers', 'sign-off', 'chain', 'offer chain'],
      },
      {
        // Admin redesign 2026-11-02 (owner, "every remaining page is option A"): its own item, gated only on Time limits (`sla`).
        id: 'time-limits',
        label: 'Time limits',
        to: '/admin/time-limits',
        object: 'admin_config',
        hint: 'How long an approval may wait, and who is told',
        keywords: ['sla', 'deadline', 'overdue', 'escalation', 'escalate', 'working hours', 'late'],
        settings: ['Each approval step', 'Approved → job created', 'Warn at', 'Escalate'],
      },
      {
        id: 'offer-letters',
        label: 'Offer letters',
        to: '/admin/offer-letters',
        object: 'hiring_setup',
        hint: 'The offer letter wording and signatory',
        keywords: ['letter', 'signatory', 'contract'],
      },
    ],
  },
  {
    id: 'messages',
    label: 'Messages',
    railLabel: 'Messages',
    icon: Mail,
    sections: [
      {
        id: 'message-templates',
        label: 'Message templates',
        to: '/admin/message-templates',
        object: 'hiring_setup',
        hint: 'The emails and messages the app sends',
        keywords: ['email template', 'wording', 'template'],
      },
      {
        id: 'email',
        label: 'Email delivery',
        to: '/admin/email',
        object: 'admin_config',
        hint: 'Every email sent, and which failed',
        keywords: ['failed', 'bounced', 'sent'],
        settings: ['Failed emails'],
        countLabel: (count) => plural(count, 'email failed today', 'emails failed today'),
      },
      {
        id: 'inbox',
        label: 'Inbound mail',
        to: '/admin/inbox',
        object: 'admin_config',
        hint: 'Replies and CVs that arrive by email',
        keywords: ['inbox', 'replies'],
        countLabel: (count) => plural(count, 'email waiting to be filed', 'emails waiting to be filed'),
      },
    ],
  },
  {
    id: 'system',
    label: 'System',
    railLabel: 'System',
    icon: Settings,
    sections: [
      {
        id: 'master-settings',
        label: 'Master settings',
        to: '/admin/master-settings',
        object: 'admin_config',
        hint: 'Switch parts of the app on or off',
        sublabel: 'Switch parts of the app on or off',
        keywords: ['modules', 'switch on', 'switch off', 'turn on', 'turn off', 'features'],
        // ADMIN-17's rows are the modules, so each one's name finds the page that switches it.
        settings: MODULE_DEFINITIONS.map((definition) => definition.label),
      },
      {
        id: 'settings',
        label: 'System settings',
        to: '/admin/settings',
        object: 'admin_config',
        hint: 'Company defaults: money, dates, logos',
        sublabel: 'Company defaults: money, dates, logos',
        keywords: ['currency', 'weekend', 'date format', 'logo', 'timezone'],
        settings: [
          'Currencies in use',
          'Reporting currency',
          'Default time zone',
          'Fiscal year starts in',
          'Date format',
          'Number format',
          'Largest CV',
          'Largest other document',
          'Keep someone on hold for',
          'Send the test straight from Shortlist',
          'Venture logos',
        ],
      },
      {
        id: 'fx-rates',
        label: 'Exchange rates',
        to: '/admin/fx-rates',
        object: 'admin_config',
        hint: 'Rates for showing money converted',
        keywords: ['fx', 'currency', 'convert'],
      },
      {
        id: 'name-matching',
        label: 'Name matching',
        to: '/admin/name-matching',
        object: 'admin_config',
        hint: 'Spellings that mean the same person',
        keywords: ['duplicates', 'same person', 'nicknames', 'spelling'],
      },
      {
        id: 'retention',
        label: 'Retention',
        to: '/admin/retention',
        object: 'admin_config',
        hint: 'How long candidate data is kept, and anonymising',
        keywords: ['privacy', 'delete', 'anonymise', 'keep data', 'gdpr', 'archive'],
        settings: ['Keep details for', 'Dry run', 'Monthly archives'],
      },
      {
        id: 'events',
        label: 'Event log',
        to: '/admin/events',
        object: 'audit_log',
        hint: 'Every change, for checking what happened',
        keywords: ['audit', 'history', 'who changed', 'who viewed'],
      },
      {
        // Admin redesign batch 2 (owner Q3): our own page. Bull Board stays reachable from it as "Technical view ↗".
        id: 'background-work',
        label: 'Background work',
        to: '/admin/background-work',
        object: 'admin_config',
        hint: 'Background jobs: sending, reading, archiving',
        keywords: ['queues', 'background jobs', 'stuck', 'sending', 'retry', 'scheduled', 'bull board'],
        settings: ['Scheduled work'],
        countLabel: (count) => plural(count, 'job failed in the last 24 hours', 'jobs failed in the last 24 hours'),
      },
    ],
  },
];

export interface AdminMenuView {
  /** Null for anyone who may not open it (HR). */
  readonly overview: AdminSection | null;
  /** Only the groups with something left in them, each with only its openable sections. */
  readonly groups: readonly AdminGroup[];
}

/** Whether this person may open the section with today's switches: the old tab rule exactly. */
export function canOpenSection(section: AdminSection, session: SessionPayload | null, modules: ModuleState): boolean {
  if (session === null || !canView(session, section.object)) return false;
  const module = moduleForPath(section.to);
  return module === null || isRequirementOn(module, modules);
}

/** R1 (§L28): a switched-off section is not drawn, and a group left empty is not drawn either. */
export function visibleAdminMenu(session: SessionPayload | null, modules: ModuleState): AdminMenuView {
  const groups = ADMIN_GROUPS.map((group) => ({
    ...group,
    sections: group.sections.filter((section) => canOpenSection(section, session, modules)),
  })).filter((group) => group.sections.length > 0);
  return { overview: canOpenSection(ADMIN_OVERVIEW, session, modules) ? ADMIN_OVERVIEW : null, groups };
}

/** Where `/admin` opens: Overview for Admin; anyone else their first page (ADR 0073); null when there is none. */
export function adminLandingPath(menu: AdminMenuView): string | null {
  if (menu.overview) return menu.overview.to;
  for (const group of menu.groups) {
    const first = group.sections.find((section) => section.isExternal !== true);
    if (first) return first.to;
  }
  return null;
}

export function isSectionActive(pathname: string, section: AdminSection): boolean {
  return [section.to, ...(section.alsoActiveOn ?? [])].some((to) => pathname === to || pathname.startsWith(`${to}/`));
}

export interface ActiveAdminSection {
  readonly section: AdminSection;
  /** Null for Overview, which sits above the groups. */
  readonly group: AdminGroup | null;
}

/** The menu item the open page belongs to, for the highlight and the phone's "Admin · People ▾" button. */
export function activeAdminSection(pathname: string, menu: AdminMenuView): ActiveAdminSection | null {
  if (menu.overview && isSectionActive(pathname, menu.overview)) return { section: menu.overview, group: null };
  for (const group of menu.groups) {
    const section = group.sections.find((candidate) => isSectionActive(pathname, candidate));
    if (section) return { section, group };
  }
  return null;
}

// --- Find a setting -----------------------------------------------------------------------------

export interface FindResult {
  /** A section itself, or a setting inside one. */
  readonly kind: 'section' | 'setting';
  readonly label: string;
  readonly section: AdminSection;
  /** The group it sits in, or "Admin" for Overview. */
  readonly where: string;
}

const MAX_SECTIONS = 8;
const MAX_SETTINGS = 8;

/** Lower case words, with a plural `s` dropped, so "holidays" finds "holiday". */
function wordsOf(query: string): readonly string[] {
  return query
    .toLowerCase()
    .split(/\s+/)
    .filter((word) => word !== '')
    .map((word) => (word.length > 3 && word.endsWith('s') ? word.slice(0, -1) : word));
}

function hasAll(text: string, words: readonly string[]): boolean {
  const lower = text.toLowerCase();
  return words.every((word) => lower.includes(word));
}

/** How well a section answers: its name first, then the words it is known by, then its one-line hint. */
function sectionRank(section: AdminSection, query: string, words: readonly string[]): number | null {
  const label = section.label.toLowerCase();
  if (label.startsWith(query)) return 0;
  if (hasAll(label, words)) return 1;
  if ((section.keywords ?? []).some((keyword) => hasAll(keyword, words))) return 2;
  if (hasAll(section.hint, words)) return 3;
  return null;
}

/**
 * A static index in the browser (brief 2026-11-02 §9): no request, so it answers as you type. Searches only what
 * this person's menu holds, so it never offers a page they would be refused at or one that is switched off.
 */
export function findInAdmin(query: string, menu: AdminMenuView): readonly FindResult[] {
  const trimmed = query.trim().toLowerCase();
  const words = wordsOf(trimmed);
  if (words.length === 0) return [];

  const placed: { readonly section: AdminSection; readonly where: string }[] = [
    ...(menu.overview ? [{ section: menu.overview, where: 'Admin' }] : []),
    ...menu.groups.flatMap((group) => group.sections.map((section) => ({ section, where: group.label }))),
  ];

  const sections = placed
    .map((entry, order) => ({ ...entry, order, rank: sectionRank(entry.section, trimmed, words) }))
    .filter((entry): entry is typeof entry & { rank: number } => entry.rank !== null)
    .sort((a, b) => a.rank - b.rank || a.order - b.order)
    .slice(0, MAX_SECTIONS)
    .map((entry): FindResult => ({ kind: 'section', label: entry.section.label, section: entry.section, where: entry.where }));

  const settings = placed
    .flatMap((entry) =>
      (entry.section.settings ?? [])
        .filter((setting) => hasAll(setting, words))
        .map((setting): FindResult => ({ kind: 'setting', label: setting, section: entry.section, where: entry.section.label })),
    )
    .slice(0, MAX_SETTINGS);

  return [...sections, ...settings];
}
